import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  Button,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from "@mui/material";
import ModalWrapper from "../../../components/model/Model";
import { useEncryptService } from "../../../shared/hooks";
const { doEncrypt, doEncryptLogin } = useEncryptService();

import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(utc);
dayjs.extend(customParseFormat);

/** Schedules from MY_SCAN_SCHEDULES: infer cloud vs on-prem from API `type`, not from the `module` route prop. */
function scheduleEnvironmentFromType(
  scansV2Flow: boolean,
  scanType: string | undefined,
  moduleProp: string | undefined
): "cloud" | "onpremises" {
  if (scansV2Flow) {
    const t = String(scanType ?? "").toLowerCase();
    if (t.startsWith("aws_")) return "cloud";
    return "onpremises";
  }
  return moduleProp === "cloud" ? "cloud" : "onpremises";
}

// interface ChangeScheduleModalProps {
//     open: boolean;
//     onClose: () => void;
//     scheduleId?: string;
//     scheduleName?: string;
//     refetch?: () => void;
//     initialValues?: any;
//     selectedRow?: any;
//     fetchScanData?: () => void;
//     module?: string;
// }

const weekdayOptions = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const ChangeScheduleModal: React.FC<any> = ({
  open,
  onClose,
  scheduleId,
  scheduleName,
  refetch,
  initialValues = {},
  selectedRow,
  fetchScanData,
  module,
  type,
  isScansV2ScheduleFlow = false,
}) => {
  const scheduleEnvironment = useMemo(
    () => scheduleEnvironmentFromType(isScansV2ScheduleFlow, type, module),
    [isScansV2ScheduleFlow, type, module]
  );

  const [frequencyOptions, setFrequencyOptions] = useState<
    { id: string; name: string; fields: string[] }[]
  >([]);
  const [dailyFrequencyOptions, setDailyFrequencyOptions] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const isDiscoveryType = String(type ?? "").toLowerCase().includes("discovery");

  const [selectedHour, setSelectedHour] = useState<string>("");
  const [selectedMinute, setSelectedMinute] = useState<string>("");
  const [selectedAmPm, setSelectedAmPm] = useState<string>("");

  // CIDR related state
  const [cidrInput, setCidrInput] = useState("");
  const [cidrBlocks, setCidrBlocks] = useState<string[]>([]);
  const [cidrError, setCidrError] = useState("");
  const [isValidatingIp, setIsValidatingIp] = useState(false);
  const [chunk, setChunk] = useState("");
  const [chunkError, setChunkError] = useState("");
  const [interval, setInterval] = useState("");
  const [intervalError, setIntervalError] = useState("");
  const [isLimitPopupOpen, setIsLimitPopupOpen] = useState(false);
  const [limitPopupMessage, setLimitPopupMessage] = useState("");
  const [bypassLimit, setBypassLimit] = useState(false);

  useEffect(() => {
    if (open && initialValues?.ip_range) {
      const parsedIpRange = Array.isArray(initialValues.ip_range)
        ? initialValues.ip_range
        : [initialValues.ip_range];
      setCidrBlocks(parsedIpRange);
    }
  }, [open, initialValues?.ip_range]);

  const cidrInputRef = useRef<HTMLInputElement>(null);
  //   const initialFormValues = module === 'onpremises'
  // ? {
  //     scheduleName: initialValues.scan_name || scheduleName || "",
  //     frequency: initialValues.frequency_id || "",
  //     time: initialValues.time || "12:00 AM",
  //     date: initialValues.date ? dayjs(initialValues.date, "DD-MM-YYYY") : null,
  //     weekday: initialValues.day || "Monday",
  //   }
  // : {
  //     frequency: initialValues.frequency_id || "",
  //     time: initialValues.time || "12:00 AM",
  //   };

  const [initialFormValues, setInitialFormValues] = useState<any>(() => {
    const env = scheduleEnvironmentFromType(isScansV2ScheduleFlow, type, module);
    const defaultScanName = initialValues.scan_name || scheduleName || "";
    if (env === "onpremises") {
      return {
        scheduleName: defaultScanName,
        frequency: "",
        time: "12:00 AM",
        date: null,
        weekday: "",
      };
    } else {
      return {
        accountId: "",
        accessKey: "",
        secretKey: "",
        scheduleName: defaultScanName,
        frequency: "",
        time: "",
        date: null,
        weekday: "",
      };
    }
  });


  useEffect(() => {
    const getFrequencyOptions = async () => {
      try {
        setIsFetching(true);
        let url = "";
        if (scheduleEnvironment === "onpremises" || scheduleEnvironment === "cloud") {
          url = END_POINTS.MY_SCAN_SCHEDULE_FREQUENCY;
        } else {
          url = "securityscan/cloud-frequency-schedule";
        }

        const response = await axiosInstance.get(url);

        if (scheduleEnvironment === "onpremises" || scheduleEnvironment === "cloud") {
          setFrequencyOptions(response.data.data.frequency);
        } else {
          setDailyFrequencyOptions(response.data.data.Daily || []);
        }
      } catch (error) {
        console.error("Error fetching frequency options:", error);
        toast.error("Failed to load frequency options");
      }
    };

    const getScheduleInfo = async () => {
      try {
        const url = END_POINTS.MY_SCAN_SCHEDULE_SCAN;
        const params = {
          schedule_scan_id: initialValues.schedule_id,
          timezone: 'Asia/Calcutta'
        };
        const response = await axiosInstance.get(url, { params });
        console.log("Schedule info response:", response.data);

        // Set initial form values based on module type
        if (scheduleEnvironment === "onpremises") {
          // Check if time is already in 12-hour format or needs conversion
          const apiTime = response.data.data?.time || "";
          const isAlready12Hour = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i.test(apiTime);
          const convertedTime = isAlready12Hour ? apiTime : (convert24To12Hour(apiTime) || "");

          const apiDate = response.data.data?.date;
          let parsedDate = null;
          if (apiDate) {
            const d = dayjs(apiDate, "DD-MM-YYYY");
            parsedDate = d.isValid() ? d : dayjs(apiDate);
          }

          const intialdata: any = {
            scheduleName: response.data.data?.schedule_name || scheduleName || "",
            frequency: response.data.data?.frequency_id || "",
            host_or_range: response.data.data?.host_or_range || "",
            time: convertedTime,
            weekday: response.data.data?.day || "",
            date: parsedDate,
          };

          setInitialFormValues(intialdata);
          parseTimeToFields(convertedTime);

          // Set CIDR and discovery related data
          if (response.data.data?.ip_range) {
            setCidrBlocks(prev => (prev && prev.length > 0) ? prev : (Array.isArray(response.data.data.ip_range) ? response.data.data.ip_range : [response.data.data.ip_range]));
          } else if (response.data.data?.host_or_range) {
            const hor = response.data.data.host_or_range;
            const parsedHor = Array.isArray(hor) ? hor : String(hor || '').split(',').map(s => s.trim()).filter(Boolean);
            setCidrBlocks(prev => (prev && prev.length > 0) ? prev : parsedHor);
          }
          if (response.data.data?.chunk_size) {
            setChunk(String(response.data.data.chunk_size));
          }
          if (response.data.data?.interval) {
            setInterval(String(response.data.data.interval));
          }

          console.log("Set onpremises initial form values:", intialdata);
        } else {
          // Check if time is already in 12-hour format or needs conversion
          const apiTime = response.data.data?.time || "";
          const isAlready12Hour = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i.test(apiTime);
          const convertedTime = isAlready12Hour ? apiTime : (convert24To12Hour(apiTime) || "");

          const apiDate = response.data.data?.date;
          let parsedDate = null;
          if (apiDate) {
            const d = dayjs(apiDate, "DD-MM-YYYY");
            parsedDate = d.isValid() ? d : dayjs(apiDate);
          }

          const intialdata: any = {
            scheduleName: response.data.data?.schedule_name || scheduleName || "",
            frequency: response.data.data?.frequency_id || initialValues.frequency_id || "",
            time: convertedTime,
            date: parsedDate,
            weekday: response.data.data?.day || "",
          };
          setInitialFormValues(intialdata);
          parseTimeToFields(convertedTime);
          console.log("Set cloud initial form values:", intialdata);
        }
      } catch (error) {
        console.error("Error fetching schedule info:", error);
        toast.error("Failed to load schedule information");
      } finally {
        setIsFetching(false);
      }
    };

    getFrequencyOptions();
    if (initialValues.schedule_id) {
      getScheduleInfo();
    } else {
      setIsFetching(false);
    }
  }, [scheduleEnvironment, initialValues.schedule_id, initialValues.time, initialValues.frequency_id, scheduleName]);

  const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m++) {
        const hour = h % 12 === 0 ? 12 : h % 12;
        const minute = m.toString().padStart(2, "0");
        const period = h < 12 ? "AM" : "PM";
        const label = `${hour}:${minute} ${period}`;
        options.push({ value: label, label });
      }
    }
    return options;
  };

  const validationSchema = useMemo(
    () =>
      scheduleEnvironment === "onpremises"
        ? Yup.object({
          scheduleName: Yup.string().required("Scan Name is required"),
          frequency: Yup.string().required("Frequency is required"),
          time: Yup.string().required("Time is required"),
          date: Yup.mixed()
            .nullable()
            .test(
              "date-required",
              "Date is required for monthly and one-time schedules",
              function (value) {
                const { frequency } = this.parent;
                const selected = frequencyOptions.find((opt) => opt.id === frequency);
                const freqName = selected?.name?.toLowerCase() || "";
                const requiresDate = freqName.includes("monthly") || freqName.includes("one time");
                if (!requiresDate) return true;
                return value != null && dayjs(value as any).isValid();
              }
            )
            .test(
              "date-not-in-past",
              "Date cannot be in the past",
              function (value) {
                const { frequency } = this.parent;
                const selected = frequencyOptions.find((opt) => opt.id === frequency);
                const freqName = selected?.name?.toLowerCase() || "";
                if (!freqName.includes("monthly") && !freqName.includes("one time")) {
                  return true;
                }
                if (!value) {
                  return true;
                }
                return !dayjs(value as any).isBefore(dayjs().startOf("day"));
              }
            ),
          weekday: Yup.string().test(
            "weekday-required",
            "Day is required for weekly schedules",
            function (value) {
              const { frequency } = this.parent;
              const selected = frequencyOptions.find((opt) => opt.id === frequency);
              const freqName = selected?.name?.toLowerCase() || "";
              return freqName.includes("weekly") ? !!value : true;
            }
          ),
        })
        : Yup.object({
          // accountId: Yup.string().required("Account ID is required"),
          // accessKey: Yup.string().required("Access Key is required"),
          // secretKey: Yup.string().required("Secret Key is required"),
          frequency: Yup.string().required("Frequency is required"),
          time: Yup.string().required("Time is required"),
        }),
    [scheduleEnvironment, frequencyOptions]
  );

  const convertToUtcTime = (time: string) => {
    const today = dayjs().format("YYYY-MM-DD");
    const dateTimeString = `${today} ${time}`;
    const utcTime = dayjs(dateTimeString, "YYYY-MM-DD hh:mm A").utc().format("HH:mm");
    return utcTime;
  };

  // Function to convert 24-hour format to 12-hour format
  const convert24To12Hour = (time24: string) => {
    if (!time24) return "12:00 AM";

    console.log("Converting time from 24-hour format:", time24);

    // Remove seconds if present (e.g., "02:04:00" -> "02:04")
    const timeWithoutSeconds = time24.split(':').slice(0, 2).join(':');

    try {
      const [hours, minutes] = timeWithoutSeconds.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const displayMinutes = minutes.toString().padStart(2, '0');
      const result = `${displayHours}:${displayMinutes} ${period}`;
      console.log("Converted to 12-hour format:", result);
      return result;
    } catch (error) {
      console.error("Error converting time format:", error);
      return "12:00 AM";
    }
  };

  // Function to parse 12-hour format and set individual time fields
  const parseTimeToFields = (time12: string) => {
    if (!time12 || time12.trim() === "") {
      setSelectedHour("12");
      setSelectedMinute("00");
      setSelectedAmPm("AM");
      return;
    }

    try {
      console.log("Parsing time:", time12);
      const [time, period] = time12.trim().split(' ');
      const [hours, minutes] = time.split(':');

      console.log("Parsed parts:", { time, period, hours, minutes });

      // Ensure we have valid values
      if (!hours || !minutes || isNaN(parseInt(hours)) || isNaN(parseInt(minutes))) {
        console.log("Invalid time format, setting defaults");
        setSelectedHour("12");
        setSelectedMinute("00");
        setSelectedAmPm("AM");
        return;
      }

      setSelectedHour(hours);
      setSelectedMinute(minutes);
      setSelectedAmPm(period || "AM");
      console.log("Set time fields:", { hours, minutes, period });
    } catch (error) {
      console.error("Error parsing time:", error);
      setSelectedHour("12");
      setSelectedMinute("00");
      setSelectedAmPm("AM");
    }
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validationSchema,
    onSubmit: async (values) => {
      console.log("Form submitted with values:", values);

      // Check if the selected frequency requires a date
      const selected = frequencyOptions.find(opt => opt.id === values.frequency);
      const requiresDate = selected?.fields.includes("date");
      const freqNameLower = String(selected?.name || "").toLowerCase();
      const isHourly = freqNameLower.includes("hour");

      // If date is required but not provided, show an error and return
      if (requiresDate && !values.date) {
        toast.error("Date is required for the selected frequency");
        return;
      }

      setLoading(true);
      try {
        const effectiveScheduleId = String(scheduleId || initialValues.schedule_id || selectedRow?.scan_uuid || "");
        const isEditingSchedule = Boolean(effectiveScheduleId);
        if (scheduleEnvironment === "onpremises") {
          const payload: any = {
            schedule_scan_id: effectiveScheduleId || selectedRow?.scan_uuid,
            scan_name: values.scheduleName,
            frequency_id: values.frequency,
            time: convertToUtcTime(`${selectedHour}:${selectedMinute} ${selectedAmPm}`),
          };

          if (isDiscoveryType) {
            if (!bypassLimit) {
              const isValid = await handleValidateIp(true);
              if (!isValid) {
                setLoading(false);
                return;
              }
            }

            const combinedIpRanges = [...cidrBlocks];
            const safeCidrInput = String(cidrInput || '');
            if (safeCidrInput.trim()) {
              const parts = safeCidrInput.trim().split(/[\n,]/).map(p => p.trim()).filter(Boolean);
              combinedIpRanges.push(...parts);
            }
            if (isEditingSchedule) {
              payload.schedule_id = effectiveScheduleId;
            }
            payload.ip_range = combinedIpRanges;
            payload.chunk_size = chunk || "16";
            payload.interval = interval || "1";
            payload.modify = true;
          }

          if (initialValues.scan_id && !isEditingSchedule) {
            payload.scan_id = initialValues.scan_id;
          }

          // Only include `date` when the frequency is NOT hourly
          if (values.date && !isHourly) {
            payload.date = dayjs(values.date, "DD-MM-YYYY").format("DD-MM-YYYY");
          }

          if (values.weekday) {
            payload.day = values.weekday;
          }

          // Detailed debug logs for inspection
          // eslint-disable-next-line no-console
          console.log("[Schedule Edit Debug] Frequency:", selected?.name);
          // eslint-disable-next-line no-console
          console.log("[Schedule Edit Debug] isHourly:", isHourly);
          // eslint-disable-next-line no-console
          console.log("[Schedule Edit Debug] Prepared payload (sending):", payload);

          const endpoint = isDiscoveryType && isEditingSchedule
            ? END_POINTS.DISCOVERY_EDIT_SCHEDULE
            : END_POINTS.MY_SCAN_SCHEDULED_SCAN;
          // eslint-disable-next-line no-console
          console.log("[Schedule Edit Debug] API endpoint:", endpoint);

          // Send payload
          const response = await axiosInstance.post(endpoint, payload);

          // eslint-disable-next-line no-console
          console.log("[Schedule Edit Debug] API Response:", response?.data);
          fetchScanData?.()
          toast.success("Schedule updated successfully");
          refetch?.();
          onClose();
        } else {
          console.log("Scheduling cloud scan with values:", values);
          console.log(scheduleId);
          console.log(selectedRow?.scan_uuid);
          console.log("scheduleName", scheduleName);
          console.log("frequency", values.frequency);
          console.log("time", values.time);
          const payload: any = {
            scan_name: values.scheduleName,
            frequency_id: values.frequency,
            time: convertToUtcTime(`${selectedHour}:${selectedMinute} ${selectedAmPm}`),
          };

          if (isEditingSchedule) {
            payload.schedule_scan_id = effectiveScheduleId || selectedRow?.scan_uuid;
            payload.schedule_id = effectiveScheduleId || selectedRow?.scan_uuid;
          } else {
            if (initialValues.scan_id) {
              payload.scan_id = initialValues.scan_id;
            }
          }

          // Only include `date` when the frequency is NOT hourly
          if (values.date && !isHourly) {
            payload.date = dayjs(values.date, "DD-MM-YYYY").format("DD-MM-YYYY");
          }

          if (values.weekday) {
            payload.day = values.weekday;
          }

          // if (values.accountId) payload.account_id = doEncryptLogin(values.accountId);
          // if (values.accessKey) payload.access_key = doEncryptLogin(values.accessKey);
          // if (values.secretKey) payload.secret_key = doEncryptLogin(values.secretKey);
          // payload.encrypt = true;

          const endpoint = isEditingSchedule
            ? "securityscan/edit-schedule"
            : END_POINTS.MY_SCAN_SCHEDULED_SCAN;

          console.log("[Schedule Edit Debug] Cloud payload (sending):", payload, "Endpoint:", endpoint);

          const response = await axiosInstance.post(endpoint, payload);
          console.log("Cloud schedule response:", response.data.data);
          toast.success(
            isEditingSchedule
              ? "Schedule updated successfully"
              : "Schedule created successfully"
          );
          refetch?.();
          onClose();

        }

      } catch (err) {
        console.error("Error scheduling scan:", err);
        toast.error((err as any).response?.data?.message || "An error occurred while scheduling the scan");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik && Object.keys(initialFormValues).length > 0 && (initialFormValues.frequency || initialFormValues.scheduleName)) {
      console.log("Updating formik values with:", initialFormValues);
      formik.setValues(initialFormValues);
      // Explicitly trigger validation after setting values
      formik.validateForm();
    }
  }, [initialFormValues]);

  // Trigger validation when frequency options load or change
  useEffect(() => {
    if (formik && frequencyOptions.length > 0 && formik.values.frequency) {
      formik.validateForm();
    }
  }, [frequencyOptions, formik.values.frequency]);

  // Initialize time fields when modal opens
  useEffect(() => {
    if (!open) return;

    if (!selectedHour || !selectedMinute || !selectedAmPm) {
      setSelectedHour("12");
      setSelectedMinute("00");
      setSelectedAmPm("AM");
    }

    // Trigger validation when modal opens with pre-filled data
    if (selectedHour && selectedMinute && selectedAmPm && formik.values.frequency) {
      formik.validateForm();
    }
  }, [open, frequencyOptions]);

  useEffect(() => {
    if (!open || !selectedHour || !selectedMinute || !selectedAmPm) return;

    const timeString = `${selectedHour}:${selectedMinute} ${selectedAmPm}`;
    if (formik.values.time !== timeString) {
      formik.setFieldValue("time", timeString);
      // Trigger validation after setting time
      formik.validateForm();
    }
  }, [open, selectedHour, selectedMinute, selectedAmPm]);

  const selectedFrequency = frequencyOptions.find(opt => opt.id === formik.values.frequency);
  const freqName = selectedFrequency?.name?.toLowerCase() || "";

  const isPastScheduleTime = React.useMemo(() => {
    if (!isScansV2ScheduleFlow || !formik.values.frequency || !selectedFrequency) return false;

    const now = dayjs();
    const getH24 = (hStr: string, ampm: string) => {
      let h = parseInt(hStr, 10);
      if (isNaN(h)) return null;
      if (ampm === "PM" && h < 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return h;
    };

    const h24 = getH24(selectedHour, selectedAmPm);
    const m = parseInt(selectedMinute, 10);
    if (h24 === null || isNaN(m)) return false;

    let formattedTime = dayjs().hour(h24).minute(m).second(0).millisecond(0);

    if (selectedFrequency.name?.toLowerCase().includes("weekly") && formik.values.weekday) {
      const currentDayName = now.format("dddd");
      if (formik.values.weekday === currentDayName) {
        return formattedTime.isBefore(now) || formattedTime.isSame(now);
      }
      return false;
    }

    if (selectedFrequency.name?.toLowerCase().includes("monthly") || selectedFrequency.name?.toLowerCase().includes("one time")) {
      if (formik.values.date) {
        formattedTime = dayjs(formik.values.date, "DD-MM-YYYY").hour(h24).minute(m).second(0).millisecond(0);
      }
      return formattedTime.isBefore(now) || formattedTime.isSame(now);
    }

    // Default check for Daily, Hourly or any other frequency that starts today
    return formattedTime.isBefore(now) || formattedTime.isSame(now);
  }, [isScansV2ScheduleFlow, formik.values.frequency, selectedFrequency, selectedHour, selectedMinute, selectedAmPm, formik.values.weekday, formik.values.date]);

  const isTimeComplete = Boolean(selectedHour && selectedMinute && selectedAmPm);
  const shouldBlockSubmit = isScansV2ScheduleFlow || scheduleEnvironment === "cloud";
  const isCidrMissing =
    scheduleEnvironment === "onpremises" &&
    isDiscoveryType &&
    cidrBlocks.length === 0 &&
    !String(cidrInput || "").trim();

  const isDateRequired = freqName.includes('monthly') || freqName.includes('one time');
  const isDateMissing = isDateRequired && (!formik.values.date || !dayjs(formik.values.date).isValid());

  const isWeekdayRequired = freqName.includes('weekly');
  const isWeekdayMissing = isWeekdayRequired && !formik.values.weekday;

  const isScheduleSubmitDisabled = shouldBlockSubmit
    ? loading || (Object.keys(formik.errors).length > 0) || !isTimeComplete || isPastScheduleTime || !!cidrError || !!chunkError || !!intervalError || isCidrMissing || isDateMissing || isWeekdayMissing || isValidatingIp
    : loading;

  const handleHourChange = (event: any) => {
    setSelectedHour(event.target.value);
    const timeString = `${event.target.value}:${selectedMinute} ${selectedAmPm}`;
    formik.setFieldValue("time", timeString);
  };

  // Handle Minute Change
  const handleMinuteChange = (event: any) => {
    setSelectedMinute(event.target.value);
    const timeString = `${selectedHour}:${event.target.value} ${selectedAmPm}`;
    formik.setFieldValue("time", timeString);
  };

  // Handle AM/PM Change
  const handleAmPmChange = (event: any) => {
    setSelectedAmPm(event.target.value);
    const timeString = `${selectedHour}:${selectedMinute} ${event.target.value}`;
    formik.setFieldValue("time", timeString);
  };

  const addCidrFromInput = () => {
    const raw = String(cidrInput || '').trim();
    if (!raw) return;

    const parts = raw
      .split(/[\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    setCidrBlocks((prev) => {
      const next = [...prev];
      parts.forEach((p) => {
        if (!next.includes(p)) {
          next.push(p);
        }
      });
      return next;
    });

    setCidrInput("");
    setCidrError("");
  };

  const handleCidrKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addCidrFromInput();
    } else if (
      (event.key === "Backspace" || event.key === "Delete") &&
      !cidrInput &&
      cidrBlocks.length
    ) {
      setCidrBlocks((prev) => prev.slice(0, -1));
      setCidrError("");
    }
  };

  const handleRemoveCidr = (block: string) => {
    setCidrBlocks((prev) => prev.filter((b) => b !== block));
    setCidrError("");
  };

  const handleValidateIp = async (isAuto = false) => {
    const combinedIpRanges = [...cidrBlocks];
    const safeCidrInput = String(cidrInput || '');
    if (safeCidrInput.trim()) {
      const parts = safeCidrInput.trim().split(/[\n,]/).map(p => p.trim()).filter(Boolean);
      combinedIpRanges.push(...parts);
    }

    if (combinedIpRanges.length === 0) {
      if (!isAuto) {
        toast.error("Please enter at least one IP range or CIDR block.");
      }
      return false;
    }

    setIsValidatingIp(true);
    setCidrError("");
    try {
      const response = await axiosInstance.post(END_POINTS.VALIDATE_IP, {
        ip_range: combinedIpRanges,
      });

      if (response.status === 200 || response.status === 201) {
        const results = response.data?.data?.results || [];
        const invalidResult = results.find((r: any) => r.is_valid === false);

        if (invalidResult) {
          setCidrError(invalidResult.error || "Invalid CIDR format");
          return false;
        }

        if (!isAuto) {
          toast.success(response.data?.message || "IP validation successful");
        }
        setCidrError("");
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error validating IP:", error);
      const errorData = error?.response?.data;
      if (errorData?.data?.within_limit === false) {
        setLimitPopupMessage(errorData?.message || "Total IP count exceeds limit");
        setIsLimitPopupOpen(true);
        setCidrError("");
        return false;
      } else if (error?.response?.status === 400) {
        setCidrError(errorData?.message || "Invalid IP address or CIDR range");
      } else {
        if (!isAuto) {
          toast.error(errorData?.message || "Invalid IP address or CIDR range");
        }
      }
      return false;
    } finally {
      setIsValidatingIp(false);
    }
  };


  return (
    <>
      <ModalWrapper
        open={open}
        onClose={onClose}
        header={
          <Typography sx={{ fontSize: "15px", fontWeight: 800 }}>
            {isDiscoveryType ? "Schedule Discovery Scan" : "Schedule Scan"}
          </Typography>
        }
        body={
          isFetching ? (
            <Box display="flex" flexDirection="column" gap={2} pb={3}>
              <Skeleton variant="text" width="60%" height={30} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
              <Box display="flex" gap={2}>
                <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rectangular" width="110px" height={48} sx={{ borderRadius: 2 }} />
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <Box display="flex" flexDirection="column" gap={2} pb={3}>
                <Typography sx={{ fontSize: "15px" }}>
                  {scheduleEnvironment === "onpremises"
                    ? (isDiscoveryType ? "Customize your discovery scan schedule." : "Customize your on-premises posture scan schedule.")
                    : "Customize your cloud scan schedule."}
                </Typography>

                {scheduleEnvironment === "onpremises" && isDiscoveryType && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Enter CIDR Block</Typography>
                    <Typography sx={{ fontSize: "12px", color: "#534F7A" }}>
                      Press Enter after each CIDR block to add multiple entries.
                    </Typography>
                    <Box
                      onClick={() => cidrInputRef.current?.focus()}
                      sx={{
                        minHeight: "48px",
                        borderRadius: "8px",
                        border: "1px solid #c7d2fe",
                        padding: "6px 10px",
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "6px",
                        // background: "#fff",
                        cursor: "text",
                      }}
                    >
                      {cidrBlocks.map((block) => (
                        <Box
                          key={block}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "3px 8px",
                            borderRadius: "999px",
                            background: "#eef2ff",
                            border: "1px solid #c7d2fe",
                            fontSize: "11px",
                            color: "#312e81",
                            cursor: "default",
                          }}
                        >
                          {block}
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCidr(block);
                            }}
                            style={{
                              cursor: "pointer",
                              marginLeft: "4px",
                              fontWeight: "bold",
                            }}
                          >
                            ×
                          </span>
                        </Box>
                      ))}
                      <input
                        ref={cidrInputRef}
                        style={{
                          border: "none",
                          outline: "none",
                          flex: 1,
                          minWidth: "140px",
                          height: "36px",
                          fontSize: "14px",
                          color: "#534F7A",
                          background: "transparent",
                        }}
                        value={cidrInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow numbers, dots, slashes, commas and spaces
                          if (/^[0-9./, ]*$/.test(val)) {
                            setCidrInput(val);
                          }
                        }}
                        onKeyDown={handleCidrKeyDown}
                        // placeholder={cidrBlocks.length === 0 ? "e.g. 192.168.1.0/24 — Press comma to separate the CIDR Block" : ""}
                        placeholder={cidrBlocks.length === 0 ? "Enter CIDR Block" : ""}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleValidateIp(false)}
                        disabled={isValidatingIp || (cidrBlocks.length === 0 && !cidrInput.trim())}
                        sx={{ textTransform: 'none', height: '32px' }}
                      >
                        {isValidatingIp ? <CircularProgress size={16} color="inherit" /> : "Validate"}
                      </Button>
                    </Box>
                    {cidrError && (
                      <Typography sx={{ fontSize: "12px", color: "red" }}>
                        {cidrError}
                      </Typography>
                    )}

                    {(cidrInput || cidrBlocks.length > 0) && (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Chunk Size"
                          value={chunk}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setChunk(val);
                              if (val && parseInt(val, 10) < 1) {
                                setChunkError("Minimum 1 chunk size is required");
                              } else if (val && parseInt(val, 10) > 16) {
                                setChunkError("Maximum 16 chunk size is allowed");
                              } else {
                                setChunkError("");
                              }
                            }
                          }}
                          size="small"
                          fullWidth
                          error={!!chunkError}
                          helperText={chunkError}
                        />
                        <TextField
                          label="Interval in minutes"
                          value={interval}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setInterval(val);
                              if (val && parseInt(val, 10) < 1) {
                                setIntervalError("Minimum interval required is 1 minutes");
                              } else {
                                setIntervalError("");
                              }
                            }
                          }}
                          size="small"
                          fullWidth
                          error={!!intervalError}
                          helperText={intervalError}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {scheduleEnvironment === "onpremises" && (
                  <>
                    <TextField
                      label="Scan Name"
                      name="scheduleName"
                      value={formik.values.scheduleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.scheduleName && Boolean(formik.errors.scheduleName)}
                      helperText={formik.touched.scheduleName && formik.errors.scheduleName ? String(formik.errors.scheduleName) : ""}
                      fullWidth
                      required
                    />

                    <FormControl fullWidth required error={formik.touched.frequency && Boolean(formik.errors.frequency)}>
                      <InputLabel id="frequency-select-label">Frequency</InputLabel>
                      <Select
                        labelId="frequency-select-label"
                        name="frequency"
                        value={formik.values.frequency}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Frequency"
                      >
                        {frequencyOptions.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {formik.touched.frequency && formik.errors.frequency ? String(formik.errors.frequency) : ""}
                      </FormHelperText>
                    </FormControl>

                    {selectedFrequency && (selectedFrequency.name?.toLowerCase().includes('monthly') || selectedFrequency.name?.toLowerCase().includes('one time')) && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Select Date"
                          value={formik.values.date}
                          onChange={(value) => formik.setFieldValue("date", value)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: Boolean(formik.touched.date && formik.errors.date),
                              helperText: formik.touched.date && formik.errors.date ? String(formik.errors.date) : "",
                            },
                          }}
                          disablePast
                          minDate={dayjs().startOf('day')}
                        />
                      </LocalizationProvider>
                    )}

                    {selectedFrequency && selectedFrequency.name?.toLowerCase().includes('weekly') && (
                      <FormControl fullWidth required error={formik.touched.weekday && Boolean(formik.errors.weekday)}>
                        <InputLabel id="weekday-select-label">Day of Week</InputLabel>
                        <Select
                          labelId="weekday-select-label"
                          name="weekday"
                          value={formik.values.weekday}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Day of Week"
                        >
                          {weekdayOptions.map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formik.touched.weekday && formik.errors.weekday ? String(formik.errors.weekday) : ""}</FormHelperText>
                      </FormControl>
                    )}
                  </>
                )}


                {scheduleEnvironment === "cloud" && (
                  <>

                    {/* <TextField
                      label="Account ID"
                      name="accountId"
                      value={formik.values.accountId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.accountId && Boolean(formik.errors.accountId)}
                      helperText={formik.touched.accountId && formik.errors.accountId ? String(formik.errors.accountId) : ""}
                      fullWidth
                      required
                    /> */}

                    {/* <TextField
                      label="Access Key"
                      name="accessKey"
                      value={formik.values.accessKey}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.accessKey && Boolean(formik.errors.accessKey)}
                      helperText={formik.touched.accessKey && formik.errors.accessKey ? String(formik.errors.accessKey) : ""}
                      fullWidth
                      required
                    /> */}

                    {/* <TextField
                      label="Secret Key"
                      name="secretKey"
                      value={formik.values.secretKey}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      type="password"
                      error={formik.touched.secretKey && Boolean(formik.errors.secretKey)}
                      helperText={formik.touched.secretKey && formik.errors.secretKey ? String(formik.errors.secretKey) : ""}
                      fullWidth
                      required
                    /> */}
                    <>
                      <TextField
                        label="Scan Name"
                        name="scheduleName"
                        value={formik.values.scheduleName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.scheduleName && Boolean(formik.errors.scheduleName)}
                        helperText={formik.touched.scheduleName && formik.errors.scheduleName ? String(formik.errors.scheduleName) : ""}
                        fullWidth
                        required
                      />

                      <FormControl fullWidth required error={formik.touched.frequency && Boolean(formik.errors.frequency)}>
                        <InputLabel id="frequency-select-label">Frequency</InputLabel>
                        <Select
                          labelId="frequency-select-label"
                          name="frequency"
                          value={formik.values.frequency}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Frequency"
                        >
                          {frequencyOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {formik.touched.frequency && formik.errors.frequency ? String(formik.errors.frequency) : ""}
                        </FormHelperText>
                      </FormControl>

                      {selectedFrequency && (selectedFrequency.name?.toLowerCase().includes('monthly') || selectedFrequency.name?.toLowerCase().includes('one time')) && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Select Date"
                            value={formik.values.date}
                            onChange={(value) => formik.setFieldValue("date", value)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: Boolean(formik.touched.date && formik.errors.date),
                                helperText: formik.touched.date && formik.errors.date ? String(formik.errors.date) : "",
                              },
                            }}
                            disablePast
                            minDate={dayjs().startOf('day')}
                          />
                        </LocalizationProvider>
                      )}

                      {selectedFrequency && selectedFrequency.name?.toLowerCase().includes('weekly') && (
                        <FormControl fullWidth required error={formik.touched.weekday && Boolean(formik.errors.weekday)}>
                          <InputLabel id="weekday-select-label">Day of Week</InputLabel>
                          <Select
                            labelId="weekday-select-label"
                            name="weekday"
                            value={formik.values.weekday}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            label="Day of Week"
                          >
                            {weekdayOptions.map((day) => (
                              <MenuItem key={day} value={day}>
                                {day}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{formik.touched.weekday && formik.errors.weekday ? String(formik.errors.weekday) : ""}</FormHelperText>
                        </FormControl>
                      )}
                    </>

                    {/* <FormControl fullWidth required error={formik.touched.frequency && Boolean(formik.errors.frequency)}>
                    <InputLabel id="daily-frequency-select">Scan Delay</InputLabel>
                    <Select
                      labelId="daily-frequency-select"
                      name="frequency"
                      value={formik.values.frequency}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Frequency"
                      displayEmpty
                    >

                      {dailyFrequencyOptions.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ textAlign: 'left', ml: '0' }}>
                      {formik.touched.frequency && formik.errors.frequency ? String(formik.errors.frequency) : "(No of hours to wait before the next scan)"}
                    </FormHelperText>
                  </FormControl> */}
                  </>
                )}

                {/* TIME SELECT (shared) */}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Hour TextField */}
                  <TextField
                    label="Hour"
                    value={selectedHour || ""}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      // Limit to 1-12 for hours
                      const numValue = parseInt(value);
                      if (numValue > 12) {
                        value = "12";
                      } else if (numValue === 0) {
                        value = "1";
                      }
                      handleHourChange({ ...e, target: { ...e.target, value } });
                    }}
                    type="text"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 1,
                      max: 12,
                      step: 1,
                      maxLength: 2
                    }}
                    required
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        height: "50px",
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: "black",
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: "black",
                        },
                      },
                    }}
                  />

                  {/* Minutes TextField */}
                  <TextField
                    label="Minutes"
                    value={selectedMinute || ""}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      // Limit to 0-59 for minutes
                      const numValue = parseInt(value);
                      if (numValue > 59) {
                        value = "59";
                      }
                      handleMinuteChange({ ...e, target: { ...e.target, value } });
                    }}
                    type="text"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                      max: 59,
                      step: 1,
                      maxLength: 2
                    }}
                    required
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        height: "50px",
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: "black",
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: "black",
                        },
                      },
                    }}
                  />

                  {/* AM/PM Dropdown */}
                  <FormControl
                    required
                  >
                    <InputLabel>AM/PM</InputLabel>
                    <Select
                      value={selectedAmPm}
                      onChange={handleAmPmChange}
                      label="AM/PM"
                      sx={{
                        borderRadius: 2,
                        height: "48px",
                        width: "110px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          //borderColor: "black", 
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "black",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "black",
                        },
                      }}
                    >
                      <MenuItem value=""></MenuItem>

                      {["AM", "PM"].map((amPm) => {

                        if (
                          amPm === "AM" || amPm === "PM"
                        ) {
                          return (
                            <MenuItem key={amPm} value={amPm}>
                              {amPm}
                            </MenuItem>
                          )
                        }

                        return null;
                      })}
                    </Select>
                  </FormControl>
                </Box>

                {isPastScheduleTime && (
                  <Typography
                    sx={{ mt: 1, fontSize: "12px", fontWeight: 500, color: "red" }}
                  >
                    Scheduled time cannot be in the past or current time.
                  </Typography>
                )}

                {/* Footer buttons */}
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button onClick={onClose} variant="outlined" sx={{ width: 120 }}>
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" variant="contained" sx={{ width: 120 }} disabled={isScheduleSubmitDisabled}>
                    {loading ? "Scheduling..." : "Schedule"}
                  </Button>
                </Box>
              </Box>
            </form>
          )
        }
      />

      <Dialog
        open={isLimitPopupOpen}
        onClose={() => setIsLimitPopupOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            padding: "8px",
            width: "350px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", textAlign: "center" }}>
          IP Limit Exceeded
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", }}>
            Large IP range detected.
            Processing this request may take significantly longer than usual. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: "12px", pb: 2 }}>
          <Button
            onClick={() => {
              setIsLimitPopupOpen(false);
              setTimeout(() => cidrInputRef.current?.focus(), 100);
            }}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "#3a2ada",
              color: "#3a2ada",
              "&:hover": { borderColor: "#3225b7", },
            }}
          >
            Edit CIDR
          </Button>
          <Button
            onClick={() => {
              setIsLimitPopupOpen(false);
              setBypassLimit(true);
            }}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#3a2ada",
              color: "#fff",
              "&:hover": { backgroundColor: "#3225b7" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeScheduleModal;

